import { useCallback, useRef } from 'react';
import { listStorageNodeContents } from '../../clients/storage_client';
import { logError, logInfo } from '../../config_and_helpers/logger';
import { listStorageContentNodes } from '../../utils/storageTree';

const LOG_CONTEXT = 'useStoragePlaceContents';

/**
 * Lots inside storage places, loaded when a card is hovered or opened.
 *
 * A hover that ends before the response arrives is cancelled. A finished list
 * is kept until `drop` clears that node, which a move or a place save does.
 * The same node is not requested twice while a call is already in flight.
 *
 * @param {string|null|undefined} token
 */
export function useStoragePlaceContents(token) {
  const cacheRef = useRef(new Map());
  const flightsRef = useRef(new Map());

  const requestNode = useCallback(
    (nodeId, { pin }) => {
      const key = String(nodeId);
      const cached = cacheRef.current.get(key);
      if (cached) return Promise.resolve(cached);
      const flight = flightsRef.current.get(key);
      if (flight) {
        if (pin) flight.pin = true;
        return flight.promise;
      }
      if (!token) return Promise.resolve({ error: true, items: [] });
      const controller = new AbortController();
      const record = { controller, pin: Boolean(pin), promise: null };
      logInfo(LOG_CONTEXT, 'fetch place contents', { nodeId: key, pinned: Boolean(pin) });
      const promise = listStorageNodeContents(token, nodeId, { signal: controller.signal }).then(
        result => {
          if (flightsRef.current.get(key) === record) flightsRef.current.delete(key);
          if (result?.cancelled) return null;
          if (!result?.success) {
            logError(LOG_CONTEXT, 'place contents failed', {
              nodeId: key,
              status: result?.status ?? null,
            });
            return { error: true, items: [] };
          }
          const entry = { error: false, items: result.data || [] };
          cacheRef.current.set(key, entry);
          logInfo(LOG_CONTEXT, 'place contents cached', {
            nodeId: key,
            lotCount: entry.items.length,
          });
          return entry;
        }
      );
      record.promise = promise;
      flightsRef.current.set(key, record);
      return promise;
    },
    [token]
  );

  const loadPlace = useCallback(
    (nodes, node, { pin = false, reload = false } = {}) => {
      const targets = listStorageContentNodes(nodes, node?.id);
      if (reload) {
        targets.forEach(target => {
          const key = String(target.id);
          cacheRef.current.delete(key);
          const flight = flightsRef.current.get(key);
          if (flight) {
            flight.controller.abort();
            flightsRef.current.delete(key);
          }
        });
        logInfo(LOG_CONTEXT, 'place contents cache cleared', {
          nodeId: node?.id ?? null,
          targetCount: targets.length,
        });
      }
      if (!targets.length) return Promise.resolve({ error: false, items: [], cancelled: false });
      return Promise.all(targets.map(target => requestNode(target.id, { pin }))).then(entries => {
        if (entries.some(entry => entry == null)) {
          return { error: false, items: [], cancelled: true };
        }
        if (entries.some(entry => entry.error)) {
          return { error: true, items: [], cancelled: false };
        }
        const items = entries.flatMap((entry, index) =>
          entry.items.map(item => ({
            ...item,
            boxId: targets[index].id,
            boxName: targets[index].name,
          }))
        );
        return { error: false, items, cancelled: false };
      });
    },
    [requestNode]
  );

  const previewPlace = useCallback(
    (nodes, node) => {
      if (!node || !token) return;
      logInfo(LOG_CONTEXT, 'preview place', { nodeId: node.id, kind: node.kind ?? null });
      loadPlace(nodes, node, { pin: false });
    },
    [loadPlace, token]
  );

  const cancelPreview = useCallback((nodes, node) => {
    if (!node) return;
    const targets = listStorageContentNodes(nodes, node.id);
    let cancelled = 0;
    targets.forEach(target => {
      const key = String(target.id);
      const flight = flightsRef.current.get(key);
      if (!flight || flight.pin) return;
      flight.controller.abort();
      flightsRef.current.delete(key);
      cancelled += 1;
    });
    if (cancelled) {
      logInfo(LOG_CONTEXT, 'preview cancelled', { nodeId: node.id, cancelled });
    }
  }, []);

  const pinPlace = useCallback(
    (nodes, node) => {
      if (!node) return;
      loadPlace(nodes, node, { pin: true });
    },
    [loadPlace]
  );

  const drop = useCallback(ids => {
    const keys = (ids || []).filter(id => id != null && id !== '').map(id => String(id));
    if (!keys.length) return;
    keys.forEach(key => {
      cacheRef.current.delete(key);
      const flight = flightsRef.current.get(key);
      if (flight) {
        flight.controller.abort();
        flightsRef.current.delete(key);
      }
    });
    logInfo(LOG_CONTEXT, 'place contents dropped', { nodeIds: keys });
  }, []);

  return { previewPlace, cancelPreview, pinPlace, loadPlace, drop };
}
